import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AddressPipe, PrettyPolicyNum } from '@amfam/policy/pipes';
// Store
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { ConfirmationModel, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { KydConfirmation } from '../../../core/store/programs/kyd';

@Component({
  selector: 'kyd-confirmation',
  templateUrl: './kyd-confirmation.component.html',
  styleUrls: ['./kyd-confirmation.component.scss'],
  standalone: false
})
export class KydConfirmationComponent implements OnInit, OnDestroy {
  @ViewChild('vehiclePolicyCellTpl', { static: true }) vehiclePolicyCellTpl: TemplateRef<unknown>;
  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;

  public model: KydConfirmation[];
  public kydFailed = false;
  public kydSuccess = false;
  public backToPoliciesText = '';

  confirmationData: ConfirmationModel;

  private enrollmentSubscription: Subscription;

  constructor(
    private pageTitle: PageTitleService,
    private copyService: CopyService,
    private location: Location,
    private store: Store
  ) {}

  public backToPolicies() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/policies']
      })
    );
  }

  ngOnInit() {
    this.pageTitle.set('KnowYourDrive Confirmation');
    this.backToPoliciesText = this.copyService.getCopy('policy.backToPolicies');

    this.enrollmentSubscription = this.store
      .select(fromRoot.getKydEnrollmentConfirmation)
      .subscribe(kydConfirmations => {
        this.model = kydConfirmations;
        if (kydConfirmations) {
          this.kydFailed = !kydConfirmations.length || kydConfirmations.some(res => !res.success);
          this.kydSuccess = kydConfirmations.length && kydConfirmations.some(res => res.success);
        }
        this.buildConfirmationData();
      });
  }

  buildConfirmationData() {
    let status: 'success' | 'error' | 'warning' = 'success';
    let heading = this.copyService.getCopy('kyd.enrollTitleSuccess');
    let nextSteps = this.copyService.getCopy('kyd.nextStepsSuccess');
    if (this.kydFailed && this.kydSuccess) {
      status = 'warning';
      heading = this.copyService.getCopy('kyd.enrollTitleWarning');
      nextSteps = this.copyService.getCopy('kyd.nextStepsWarning');
    } else if (this.kydFailed && !this.kydSuccess) {
      status = 'error';
      heading = this.copyService.getCopy('kyd.enrollTitleError');
      nextSteps = this.copyService.getCopy('kyd.nextStepsError');
    }

    this.confirmationData = {
      confirmation: {
        status: status,
        heading: heading
      },
      details: [
        {
          heading: 'Details',
          tableColumns: this.buildColumnHeaders(),
          tableRows: this.buildRowData()
        }
      ],
      nextSteps: {
        heading: 'Next Steps',
        text: nextSteps
      },
      disclaimer: this.copyService.getCopy('kyd.disclaimer')
    };
  }

  buildColumnHeaders() {
    return [
      {
        cellTemplate: this.vehiclePolicyCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Vehicle',
        prop: 'vehicle',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 150
      },
      {
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: false,
        name: 'Shipping Address',
        shortName: 'Address',
        prop: 'address',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 150
      },
      {
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: false,
        name: 'Status',
        prop: 'status',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 150
      }
    ];
  }

  buildRowData() {
    const rowData = [];
    for (const confirmation of this.model) {
      const address = confirmation.risk.shippingAddress
        ? new AddressPipe().transform(confirmation.risk.shippingAddress)
        : 'Device already with customer';

      const status = confirmation.success ? 'Enrolled' : 'Not Enrolled';

      rowData.push({
        vehicle: confirmation.risk.description,
        policy: new PrettyPolicyNum().transform(confirmation.risk.policy),
        address: address,
        status: status
      });
    }
    return rowData;
  }

  ngOnDestroy() {
    if (this.enrollmentSubscription) {
      this.enrollmentSubscription.unsubscribe();
    }
  }
}
